/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Inject, Injectable, InjectionToken, Provider} from '@angular/core';
import * as _ from 'lodash';
import {AppEventBusService} from './common/events/app-event-bus.service';

/** Browser window — injectable for tests; Angular has no built-in WINDOW token here. */
export const WINDOW = new InjectionToken<Window>('BrowserWindow');

/**
 * Thin wrapper around the browser URL for query-param reads/writes and
 * same-document navigation. Prefer this over scattering history/URL logic.
 */
@Injectable({
  providedIn: 'root'
})
export class BrowserLocationService {
  constructor(
    @Inject(WINDOW) private readonly window: Window,
    private readonly eventBus: AppEventBusService
  ) {
  }

  get href(): string {
    return this.window.location.href;
  }

  get hostname(): string {
    return this.window.location.hostname;
  }

  get pathname(): string {
    return this.window.location.pathname;
  }

  get protocol(): string {
    return this.window.location.protocol.replace(/:$/, '');
  }

  get port(): number {
    const port = this.window.location.port;
    if (port) {
      return parseInt(port, 10);
    }
    return this.protocol === 'https' ? 443 : 80;
  }

  /** Path + search + hash (no origin). */
  get url(): string {
    return this.window.location.pathname + this.window.location.search + this.window.location.hash;
  }

  get queryParams(): Record<string, any> {
    const params: Record<string, any> = {};
    const url = new URL(this.window.location.href);
    url.searchParams.forEach((paramValue, key) => {
      if (params[key] === undefined) {
        params[key] = paramValue;
      } else {
        params[key] = _.concat(params[key], paramValue);
      }
    });
    return params;
  }

  setQueryParam(key: string, value: any): void {
    const url = new URL(this.window.location.href);
    // No-op deletes must not rewrite the URL: URLSearchParams stringifies
    // spaces as "+" while UI-Router uses "%20", which would push a duplicate
    // history entry and break Back after global search.
    if ((value === null || value === undefined) && !url.searchParams.has(key)) {
      return;
    }
    this.writeSearchValue(url.searchParams, key, value);
    this.replaceUrl(url);
  }

  setQueryParams(params: Record<string, any>): void {
    const url = new URL(this.window.location.href);
    url.search = '';
    _.forEach(params, (paramValue, key) => this.writeSearchValue(url.searchParams, key, paramValue));
    this.replaceUrl(url);
  }

  navigate(url: string): void {
    this.replaceUrl(new URL(url, this.window.location.origin));
  }

  private writeSearchValue(searchParams: URLSearchParams, key: string, value: any): void {
    searchParams.delete(key);
    if (value === null || value === undefined) {
      return;
    }
    (_.isArray(value) ? value : [value]).forEach(item => searchParams.append(key, `${item}`));
  }

  /** Treat "+"/ "%20" and other encoding-only differences as the same URL. */
  private hrefsAreEquivalent(a: string, b: string): boolean {
    if (a === b) {
      return true;
    }
    try {
      const urlA = new URL(a);
      const urlB = new URL(b);
      if (urlA.origin !== urlB.origin || urlA.pathname !== urlB.pathname || urlA.hash !== urlB.hash) {
        return false;
      }
      return urlA.searchParams.toString() === urlB.searchParams.toString();
    } catch {
      return false;
    }
  }

  private replaceUrl(url: URL): void {
    if (this.hrefsAreEquivalent(url.href, this.window.location.href)) {
      return;
    }
    const previousUrl = this.window.location.href;
    const event = this.eventBus.emit('$locationChangeStart', url.href, previousUrl);
    if (event.defaultPrevented) {
      return;
    }
    this.window.history.pushState(this.window.history.state, '', url.toString());
    this.window.dispatchEvent(new PopStateEvent('popstate', {state: this.window.history.state}));
    this.eventBus.emit('$locationChangeSuccess', url.href, previousUrl);
  }
}

export const angularPlatformProviders: Provider[] = [
  {provide: WINDOW, useFactory: () => window}
];
